"use client";

import { useState, useEffect } from "react";
import { siteConfig } from "@/lib/siteConfig";
import {
    Building2, ScrollText, Copyright, Key, ShieldAlert,
    RotateCcw, Package, Headphones, Bell, ArrowRight,
    Phone, Mail,
} from "lucide-react";
import MailtoLink from "@shared/MailtoLink";

/* ─── Table of contents ──────────────────────────────── */
const SECTIONS = [
    { id: "about", title: "About Us", icon: Building2 },
    { id: "terms", title: "Terms of Use", icon: ScrollText },
    { id: "copyright", title: "Copyright", icon: Copyright },
    { id: "license", title: "License", icon: Key },
    { id: "liability", title: "Limitation of Liability", icon: ShieldAlert },
    { id: "return", title: "Return Policy", icon: RotateCcw },
    { id: "shipping", title: "Shipping Policy", icon: Package },
    { id: "support", title: "Support & Bug Fixing", icon: Headphones },
    { id: "changes", title: "Notification of Changes", icon: Bell },
    { id: "contact", title: "Contact Us", icon: Phone },
];

/* ─── Helpers ────────────────────────────────────────── */
function SectionCard({ id, icon: Icon, title, children }) {
    return (
        <div
            id={id}
            className="scroll-mt-28 rounded-lg bg-white/70 backdrop-blur-xl border border-white/80 shadow-sm p-4 sm:p-7 md:p-7 lg:p-7"
        >
            <div className="flex items-center gap-3 mb-5">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 border border-primary/15">
                    <Icon size={16} className="text-primary" />
                </span>
                <h2 className="text-[18px] font-semibold text-ink tracking-tight">{title}</h2>
            </div>
            <div className="flex flex-col gap-3 text-sm text-[#5B6478] leading-relaxed">
                {children}
            </div>
        </div>
    );
}

function BulletList({ items }) {
    return (
        <ul className="flex flex-col gap-2 mt-1">
            {items.map((item, i) => (
                <li key={i} className="flex items-start gap-2.5">
                    <ArrowRight size={13} className="mt-0.5 shrink-0 text-primary" />
                    <span>{item}</span>
                </li>
            ))}
        </ul>
    );
}

/* ─── Page component ─────────────────────────────────── */
export default function Terms() {
    const [activeSection, setActiveSection] = useState("about");

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setActiveSection(entry.target.id);
                });
            },
            { rootMargin: "-20% 0px -70% 0px" }
        );
        SECTIONS.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    const scrollTo = (id) =>
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

    return (
        <>
            <section className="relative bg-[#f7f8fc] py-14 lg:py-20 px-0 md:px-4 lg:px-4 lg:px-0">
                <div className="absolute pointer-events-none top-0 right-0 w-[30rem] h-[30rem] rounded-full bg-[radial-gradient(circle,rgba(255,194,209,0.35)_0%,transparent_65%)] blur-3xl" />
                <div className="relative max-w-[1400px] mx-auto">
                    <div className="flex flex-col lg:flex-row gap-10 items-start">
                        <aside className="hidden lg:block w-64 xl:w-72 shrink-0 sticky top-30">
                            <div className="rounded-lg bg-white/70 backdrop-blur-xl border border-white/80 shadow-sm p-5">
                                <p className="font-semibold mb-2 text-ink">
                                    Table of Contents
                                </p>
                                <nav className="flex flex-col gap-0.5">
                                    {SECTIONS.map(({ id, title, icon: Icon }) => (
                                        <button
                                            key={id}
                                            onClick={() => scrollTo(id)}
                                            className={`flex items-center gap-2.5 w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-150 cursor-pointer ${activeSection === id
                                                ? "bg-primary/10 text-primary font-semibold"
                                                : "text-ink/55 hover:text-ink hover:bg-black/[0.04]"
                                                }`}
                                        >
                                            <Icon size={14} className="shrink-0" />
                                            {title}
                                        </button>
                                    ))}
                                </nav>
                            </div>
                        </aside>

                        <div className="flex-1 min-w-0 flex flex-col gap-5 px-4 sm:px-0">

                            <SectionCard id="about" icon={Building2} title="About Us">
                                <p>Velan is a successful outsourcing service provider with multiple strategic business units. The company has been assessed for ISO 9001:2008 standard.</p>
                                <p>Velan is best described as a business process outsourcing company specializing in the fields of Managed, Contact, HealthCare, Financial and Data Services delivering to customers across the globe.</p>
                                <p>Velan is a full-service creative firm. With the mission to create an effective presence for our clients by combining industry-leading technology with inventive design, our integrated approach always begins with the need of the customer. We produce visually compelling designs that are user friendly, based on the client&apos;s requirements and delivered on schedule. At Velan, we hold ourselves to the highest standards of design and business excellence.</p>
                            </SectionCard>

                            <SectionCard id="terms" icon={ScrollText} title="Terms of Use">
                                <p>Please read the following text containing the terms and conditions carefully before using velanapps.com. By purchasing products from velanapps.com it is considered that you read and agree to our terms and conditions.</p>
                                <div className="rounded-lg bg-primary/5 border border-primary/10 px-4 py-3 flex items-start gap-3 mt-1">
                                    <ShieldAlert size={15} className="text-primary mt-0.5 shrink-0" />
                                    <p>Continued use of this website constitutes your acceptance of these terms. If you do not agree, please discontinue use of velanapps.com.</p>
                                </div>
                            </SectionCard>

                            <SectionCard id="copyright" icon={Copyright} title="Copyright">
                                <p>Redistribution or republication of any part of this site or its content is prohibited without the express written consent of Velan Business Solutions Inc. (Velan).</p>
                                <p>Velan is the copyright holder of all software sold on this site. The copying, redistribution, use or publication by you of any products without permission is strictly prohibited.</p>
                                <BulletList items={[
                                    "You are not allowed to use any part of our code in whole or in part of any other software, product, or website",
                                    "Refunds, support service and bug fixing service are not available to violators of copyright",
                                ]} />
                            </SectionCard>

                            <SectionCard id="license" icon={Key} title="License">
                                <p>Only one copy of an extension can be used on one Magento installation solely for your own personal or business use.</p>
                                <div className="rounded-lg bg-primary/5 border border-primary/10 px-4 py-3 flex items-start gap-3 mt-1">
                                    <ArrowRight size={14} className="text-primary mt-0.5 shrink-0" />
                                    <p>A separate License must be purchased for each Magento installation. Sharing a single license across multiple installations is not permitted.</p>
                                </div>
                            </SectionCard>

                            <SectionCard id="liability" icon={ShieldAlert} title="Limitation of Liability">
                                <p>You acknowledge that the Software you purchase has not been developed to meet your individual requirements, and that it is therefore your responsibility to ensure that the facilities and functions of the Software as described in the Documentation meet your requirements.</p>
                                <p>We shall not under any circumstances be liable to you, whether in contract, tort (including negligence), breach of statutory duty, or otherwise, for:</p>
                                <div className="flex flex-col gap-2 mt-1">
                                    {[
                                        "Loss of profits, sales, business, or revenue",
                                        "Business interruption",
                                        "Loss of anticipated savings",
                                        "Loss or corruption of data or information",
                                        "Loss of business opportunity, goodwill or reputation",
                                        "Any indirect or consequential loss or damage",
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-start gap-3 rounded-lg bg-red-50 border border-primary/10 px-4 py-2.5">
                                            <ArrowRight size={14} className="mt-0.5 shrink-0 text-primary" />
                                            <span>{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </SectionCard>

                            <SectionCard id="return" icon={RotateCcw} title="Return Policy">
                                <p>We offer a <strong className="font-semibold text-ink">10-day money back guarantee</strong> for all our extensions. You can get a full refund if you are not satisfied with any of our extensions during 10 days after the purchase, unless copyright is infringed.</p>
                                <BulletList items={[
                                    "To get the refund, please contact us — refund will be provided within 7 days of your request",
                                    "Fees for installation, conflicts resolution and customization services are not refundable",
                                    "Refunds are not available to violators of copyright",
                                ]} />
                            </SectionCard>

                            <SectionCard id="shipping" icon={Package} title="Shipping Policy">
                                <p>Digital products purchased through Velanapps website (Velan Business Solutions Inc.) are delivered to you automatically at time of purchase.</p>
                                <p>You will be provided with an activation code within a few hours once you successfully purchase the product.</p>
                                <div className="rounded-lg bg-primary/5 border border-primary/10 px-4 py-3 flex items-start gap-3 mt-1">
                                    <Mail size={15} className="text-primary mt-0.5 shrink-0" />
                                    <p>
                                        If there is a delay or you can&apos;t find the email, contact our customer service team at{" "}
                                        <MailtoLink email={siteConfig.emailSupport} className="text-primary underline-offset-2">{siteConfig.emailSupport}</MailtoLink>.
                                    </p>
                                </div>
                            </SectionCard>

                            <SectionCard id="support" icon={Headphones} title="Support and Bug Fixing Policy">
                                <p>Installation support is absolutely <strong className="font-semibold text-ink">FREE</strong> when you purchase our products. With the free versions it comes with a nominal fee.</p>
                                <BulletList items={[
                                    "Customers are free to report any bugs experienced while using our extensions",
                                    "Velan will try to fix reported bugs as soon as possible",
                                    "To report bugs or request support, please contact us",
                                ]} />
                            </SectionCard>

                            <SectionCard id="changes" icon={Bell} title="Notification of Changes">
                                <p>Velan reserves the right to change these conditions from time to time as it sees fit. Your continued use of the site will signify your acceptance of any adjustment to these terms.</p>
                                <p>Any change to these terms will be published at this page. We encourage you to review this page periodically to stay informed of any updates.</p>
                            </SectionCard>

                            <div id="contact" className="scroll-mt-28 rounded-lg bg-ink text-white overflow-hidden">
                                <div className="p-8">
                                    <div className="flex items-center gap-2.5 mb-1">
                                        <Phone size={16} className="text-primary" />
                                        <h2 className="text-xl font-semibold">Contact Us</h2>
                                    </div>
                                    <p className="text-white/55 text-sm mb-7">
                                        Have questions about our terms, licensing, or need support? Get in touch with our team.
                                    </p>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                                        <div>
                                            <p className="text-md font-semibold text-white/50 mb-3">
                                                General Enquiries
                                            </p>
                                            <div className="inline-flex flex-col gap-3">
                                                <MailtoLink email={siteConfig.emailInfo} className="flex items-center gap-2.5 text-sm text-white/85 hover:text-white transition-colors">
                                                    <Mail size={14} className="text-primary shrink-0" />
                                                    {siteConfig.emailInfo}
                                                </MailtoLink>
                                                <a href={`tel:${siteConfig.phoneTel}`} className="flex items-center gap-2.5 text-sm text-white/85 hover:text-white transition-colors">
                                                    <Phone size={14} className="text-primary shrink-0" />
                                                    {siteConfig.phone}
                                                </a>
                                                <a href={`tel:${siteConfig.phoneIndia1Tel}`} className="flex items-center gap-2.5 text-sm text-white/85 hover:text-white transition-colors">
                                                    <Phone size={14} className="text-primary shrink-0" />
                                                    {siteConfig.phoneIndia1}
                                                </a>
                                            </div>
                                        </div>

                                        <div>
                                            <p className="text-md font-semibold text-white/50 mb-3">
                                                Product Support
                                            </p>
                                            <div className="inline-flex flex-col gap-3">
                                                <MailtoLink email={siteConfig.emailSupport} className="flex items-center gap-2.5 text-sm text-white/85 hover:text-white transition-colors">
                                                    <Mail size={14} className="text-primary shrink-0" />
                                                    {siteConfig.emailSupport}
                                                </MailtoLink>
                                                <p className="text-sm text-white/40 leading-relaxed">
                                                    For installation support, bug reports, and product-related queries.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="h-1.5 bg-red-gradient" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}